import React from 'react';
import {
  Paper, Box, Typography, Chip, IconButton, Tooltip, Stack
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  NetworkCheck as TestIcon,
  Cloud as CloudIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Block as BlockIcon
} from '@mui/icons-material';

const providerLabels = {
  dropbox: 'Dropbox',
  gdrive: 'Google Drive',
  gcs: 'Google Cloud Storage',
  'aws-s3': 'AWS S3',
  sftp: 'SFTP/SCP'
};

export default function ExternalStorageCard({ config, onEdit, onDelete, onTest, testing }) {
  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 1.5 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box sx={{ flex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <CloudIcon fontSize="small" color="primary" />
            <Typography variant="subtitle1" fontWeight="medium">
              {config.display_name}
            </Typography>
            <Chip
              label={providerLabels[config.provider] || config.provider}
              size="small"
              color="primary"
              variant="outlined"
            />
            {config.derived_from_org_config_id && (
              <Chip
                label={config.derived_org_name ? `Org: ${config.derived_org_name}` : 'Organization storage'}
                size="small"
                color="secondary"
                variant="outlined"
              />
            )}
            {!config.enabled && (
              <Chip label="Disabled" size="small" color="default" />
            )}
            {config.auth_paused && (
              <Tooltip title={config.last_auth_error
                ? `Transfers paused after an authentication failure: ${config.last_auth_error}. Re-authenticate and run Test Connection to resume.`
                : 'Transfers paused after repeated authentication failures. Re-authenticate and run Test Connection to resume.'}>
                <Chip
                  icon={<BlockIcon />}
                  label="Paused — auth failed"
                  size="small"
                  color="error"
                />
              </Tooltip>
            )}
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
            Root: {config.destination_path}
          </Typography>

          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
            Template: {
              config.custom_path
                || (config.file_organization === 'by_project_target' && '$PROJECT/$TARGET')
                || (config.file_organization === 'by_target' && '$TARGET')
                || 'Flat — files placed directly in the root path'
            }
          </Typography>

          <Stack direction="row" spacing={1} alignItems="center">
            {config.transfer_content && config.transfer_content !== 'raw' && (
              <Chip
                label={config.transfer_content === 'calibrated' ? 'Calibrated only' : 'Raw + Calibrated'}
                size="small"
                color="info"
                variant="outlined"
              />
            )}
            {config.delete_from_minio && (
              <Chip label="Auto-delete from MinIO" size="small" color="warning" variant="outlined" />
            )}
            {config.compress_before_transfer !== false && (
              <Chip label="FZ Compress" size="small" color="primary" variant="outlined" />
            )}
            {config.owner_type === 'organization' && config.default_selected && (
              <Chip label="Selected by default" size="small" color="success" variant="outlined" />
            )}
          </Stack>

          {config.comment && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              {config.comment}
            </Typography>
          )}

          {config.last_test_at && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
              {config.last_test_result === 'success' ? (
                <SuccessIcon fontSize="small" color="success" />
              ) : (
                <ErrorIcon fontSize="small" color="error" />
              )}
              <Typography variant="caption" color="text.secondary">
                Last test: {new Date(config.last_test_at).toLocaleString()} -{' '}
                {config.last_test_result === 'success' ? 'Connected' : config.last_test_error || 'Failed'}
              </Typography>
            </Box>
          )}
        </Box>

        {(onTest || onEdit || onDelete) && (
          <Stack direction="row" spacing={0.5}>
            {onTest && (
              <Tooltip title="Test Connection">
                <IconButton size="small" onClick={() => onTest(config._id)} disabled={testing}>
                  <TestIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {onEdit && (
              <Tooltip title="Edit">
                <IconButton size="small" onClick={() => onEdit(config)}>
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {onDelete && (
              <Tooltip title="Delete">
                <IconButton size="small" onClick={() => onDelete(config)} color="error">
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        )}
      </Box>
    </Paper>
  );
}
